import React, { useEffect, useState } from 'react';
import { fetchCompanyBranding, updateCompanyBranding } from '../../services/companyCustomizationApi';
import type { CompanyBranding } from '../../services/companyCustomizationApi';
import { useI18n } from '../../contexts/I18nContext';

interface CompanyBrandingManagerProps {
  companyId: string;
  companyName: string;
}

const defaultBrandingState: CompanyBranding = {
  logoUrl: '',
  faviconUrl: '',
  primaryColor: '#4f46e5',
  secondaryColor: '#4338ca',
  accentColor: '#14b8a6',
  customCss: '',
};

const CompanyBrandingManager: React.FC<CompanyBrandingManagerProps> = ({ companyId, companyName }) => {
  const { t } = useI18n();
  const [branding, setBranding] = useState<CompanyBranding>(defaultBrandingState);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const loadBranding = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchCompanyBranding(companyId);
      if (data) {
        setBranding({
          ...defaultBrandingState,
          ...data,
        });
      } else {
        setBranding(defaultBrandingState);
      }
    } catch (err) {
      console.error('[CompanyBrandingManager] Failed to load branding', err);
      setError(t('customization.branding.errorLoad'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadBranding();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const handleChange = (field: keyof CompanyBranding, value: string) => {
    setMessage(null);
    setError(null);
    setBranding((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSave = async (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      await updateCompanyBranding(companyId, branding);
      setMessage('Branding atualizado com sucesso.');
    } catch (err) {
      console.error('[CompanyBrandingManager] Failed to save branding', err);
      setError(t('customization.branding.errorSave'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1fr)',
        gap: '24px',
        alignItems: 'flex-start',
        flexWrap: 'wrap',
      }}
    >
      <form
        onSubmit={handleSave}
        style={{
          backgroundColor: '#fff',
          borderRadius: '12px',
          border: '1px solid #e5e7eb',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          gap: '16px',
        }}
      >
        <div>
          <h3 style={{ margin: '0 0 4px 0', fontSize: '18px' }}>Identidade Visual</h3>
          <p style={{ margin: 0, color: '#6b7280', fontSize: '13px' }}>{t('customization.branding.subtitle')}</p>
        </div>

        {(message || error) && (
          <div
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: message ? '1px solid #86efac' : '1px solid #fecaca',
              backgroundColor: message ? '#dcfce7' : '#fee2e2',
              color: message ? '#166534' : '#b91c1c',
            }}
          >
            {message || error}
          </div>
        )}

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          URL do logotipo
          <input
            type="url"
            value={branding.logoUrl ?? ''}
            onChange={(event) => handleChange('logoUrl', event.target.value)}
            placeholder="https://cdn.exemplo.com/logo.png"
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
        </label>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          URL do favicon
          <input
            type="url"
            value={branding.faviconUrl ?? ''}
            onChange={(event) => handleChange('faviconUrl', event.target.value)}
            placeholder="https://cdn.exemplo.com/favicon.ico"
            style={{
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
            }}
          />
        </label>

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '12px' }}>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
            Cor primária
            <input
              type="color"
              value={branding.primaryColor ?? '#4f46e5'}
              onChange={(event) => handleChange('primaryColor', event.target.value)}
              style={{ width: '100%', height: '40px', border: '1px solid #d1d5db', borderRadius: '6px' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
            Cor secundária
            <input
              type="color"
              value={branding.secondaryColor ?? '#4338ca'}
              onChange={(event) => handleChange('secondaryColor', event.target.value)}
              style={{ width: '100%', height: '40px', border: '1px solid #d1d5db', borderRadius: '6px' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
            Cor de destaque
            <input
              type="color"
              value={branding.accentColor ?? '#14b8a6'}
              onChange={(event) => handleChange('accentColor', event.target.value)}
              style={{ width: '100%', height: '40px', border: '1px solid #d1d5db', borderRadius: '6px' }}
            />
          </label>
        </div>

        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '14px' }}>
          CSS personalizado
          <textarea
            value={branding.customCss ?? ''}
            onChange={(event) => handleChange('customCss', event.target.value)}
            rows={5}
            placeholder=".ahk-button { border-radius: 999px; }"
            style={{
              width: '100%',
              padding: '10px 12px',
              borderRadius: '6px',
              border: '1px solid #d1d5db',
              fontFamily: 'monospace',
              fontSize: '13px',
            }}
          />
        </label>

        <button
          type="submit"
          disabled={saving || loading}
          style={{
            padding: '12px 18px',
            backgroundColor: '#2563eb',
            borderRadius: '8px',
            border: 'none',
            color: '#fff',
            fontWeight: 600,
            cursor: 'pointer',
          }}
        >
          {saving ? 'Salvando...' : 'Salvar branding'}
        </button>
      </form>

      <div
        style={{
          backgroundColor: '#111827',
          borderRadius: '12px',
          border: '1px solid #1f2937',
          padding: '24px',
          color: '#f3f4f6',
          minHeight: '100%',
        }}
      >
        <p style={{ margin: '0 0 16px 0', color: '#9ca3af', fontSize: '13px' }}>Preview em tempo real</p>
        <div
          style={{
            borderRadius: '12px',
            padding: '20px',
            backgroundColor: branding.primaryColor || '#4f46e5',
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: '12px',
            boxShadow: '0 20px 45px rgba(0,0,0,0.25)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
            {branding.logoUrl ? (
              <img
                src={branding.logoUrl}
                alt={`${companyName} logo preview`}
                style={{ width: '48px', height: '48px', objectFit: 'contain' }}
                onError={(event) => {
                  (event.target as HTMLImageElement).style.display = 'none';
                }}
              />
            ) : (
              <div
                style={{
                  width: '48px',
                  height: '48px',
                  borderRadius: '12px',
                  backgroundColor: branding.accentColor || '#14b8a6',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontWeight: 700,
                  fontSize: '18px',
                }}
              >
                {companyName.substring(0, 2).toUpperCase()}
              </div>
            )}
            <div>
              <strong style={{ fontSize: '18px' }}>{companyName}</strong>
              <p style={{ margin: 0, color: '#e0e7ff', fontSize: '13px' }}>Tema personalizado ativo</p>
            </div>
          </div>
          <div
            style={{
              backgroundColor: branding.secondaryColor || '#4338ca',
              borderRadius: '12px',
              padding: '16px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
            }}
          >
            <strong>Componentes principais</strong>
            <p style={{ margin: 0, fontSize: '13px', color: '#e0e7ff' }}>{t('customization.branding.gradientHint')}</p>
            <button
              type="button"
              style={{
                alignSelf: 'flex-start',
                padding: '8px 14px',
                borderRadius: '999px',
                border: 'none',
                backgroundColor: branding.accentColor || '#14b8a6',
                color: '#0f172a',
                fontWeight: 600,
                cursor: 'default',
              }}
            >
              CTA de exemplo
            </button>
          </div>
          {branding.customCss && (
            <pre
              style={{
                backgroundColor: '#0f172a',
                borderRadius: '8px',
                padding: '12px',
                fontSize: '12px',
                fontFamily: 'monospace',
                margin: 0,
              }}
            >
              {branding.customCss}
            </pre>
          )}
        </div>
      </div>
    </div>
  );
};

export default CompanyBrandingManager;

